import {
  DataPopper,
  DataPopperRow,
  RatioProgressBar,
  tokens,
  Typography,
  Box,
  Stack,
} from '@cast/design-system';

import { DataTooltip } from '@cast/components-data-tooltip';

type ResourceBreakdownCellProps = {
  summaryUnit: string;
  isDiscovered: boolean;
  testId: string;
  used: number;
  requested: number;
  total: number;
  convertToUnit?: (value: number) => [number, string];
};

export const ResourceBreakdownCell = ({
  summaryUnit,
  isDiscovered,
  testId,
  used,
  requested,
  total,
  convertToUnit = (value: number) => [value, ''],
}: ResourceBreakdownCellProps) => {
  const [totalValue, totalUnit] = convertToUnit(total);
  const [usedValue, usedUnit] = convertToUnit(used);
  const [requestedValue, requestedUnit] = convertToUnit(requested);

  if (!total) {
    return (
      <Typography
        variant="P12R"
        sx={{ color: tokens.theme.disabled.fg }}
        fontStyle="italic"
      >
        —
      </Typography>
    );
  }

  return (
    <Stack gap={3}>
      <Typography variant="P12R">
        <span data-value={totalValue}>{totalValue}</span> {summaryUnit}
      </Typography>
      <DataTooltip
        sx={
          isDiscovered
            ? {
                width: 244,
                backgroundColor: tokens.theme.bg.structural.surfaceInverse,
                '--ds-tooltip-background':
                  tokens.theme.bg.structural.surfaceInverse,
              }
            : undefined
        }
        title={
          isDiscovered ? (
            <DataPopper
              testId={`${testId}-tooltip`}
              sx={{
                padding: 0,
                overflow: 'hidden',
                backgroundColor: tokens.theme.bg.structural.surfaceInverse,
              }}
            >
              <DataPopperRow
                sx={{
                  padding: 4,
                  height: 'unset',
                  color: tokens.theme.fg.structural.primaryOnInverse,
                }}
              >
                Connect the cluster to view utilization.
              </DataPopperRow>
            </DataPopper>
          ) : (
            <DataPopper testId={`${testId}-tooltip`}>
              <DataPopperRow
                indicator={{ color: tokens.theme.data.green100 }}
                left="Used"
                right={`${usedValue} ${usedUnit}`}
              />
              <DataPopperRow
                indicator={{ color: tokens.theme.data.blue300 }}
                left="Requested"
                right={`${requestedValue} ${requestedUnit}`}
              />
              <DataPopperRow
                indicator={{
                  color: tokens.theme.data.blue600,
                  variant: 'outlined',
                }}
                left="Allocatable"
                right={`${totalValue} ${totalUnit}`}
              />
            </DataPopper>
          )
        }
      >
        <Box width="100%" data-testid={`${testId}-progress-bar`}>
          <RatioProgressBar
            sx={{
              border: isDiscovered
                ? tokens.theme.data.grey400
                : tokens.theme.data.blue700,
              cursor: 'pointer',
            }}
            total={total}
            bars={[
              {
                value: used,
                color: tokens.theme.data.green100,
                testId: 'used-bar',
              },
              {
                value: Math.max(requested - used, 0),
                color: tokens.theme.data.blue300,
                testId: 'requested-bar',
              },
              {
                value: isDiscovered ? total : 0,
                color: tokens.theme.data.grey100,
                testId: 'total-bar',
              },
            ]}
          />
        </Box>
      </DataTooltip>
    </Stack>
  );
};
